import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
} from "@mui/material";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import { message } from "../Constants/message.js";
import SSLCertificateCheckerContent from "../Contents/SSLCertificateCheckerContent.js";
import CommentSection from "../Components/CommentSection.js";

function SSLCertificateChecker({ user, login, isLoggedIn }) {
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setDomain(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleDomainChange = (e) => {
    setDomain(e.target.value);
  };

  const handleCheckSSL = async () => {
    setSuccess("");
    setError("");
    setResult(null);
  
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }
  
    if (!domain.trim()) {
      setError("Please enter a domain to check.");
      return;
    }
  
    const domainToValidate = domain.trim();
  
    // Simple regex to validate a domain name (e.g. example.com, sub.domain.co.uk)
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
  
    if (!domainRegex.test(domainToValidate.replace(/^https?:\/\//, "").split("/")[0])) {
      setError("Invalid domain format. Please enter a valid domain like example.com.");
      return;
    }
  
    if (
      isLoggedIn &&
      user.account_type.toLowerCase() === "free" &&
      credits.remaining < 1
    ) {
      setError("Insufficient credits to check SSL. Please upgrade your plan.");
      return;
    }
  
    setLoading(true);
    let domainToCheck = domainToValidate;
  
    try {
      const parsedUrl = new URL(
        domainToCheck.startsWith("http")
          ? domainToCheck
          : `https://${domainToCheck}`
      );
      domainToCheck = parsedUrl.hostname;
    } catch (e) {
      setError("Invalid URL format. Please enter a valid domain or URL.");
      return;
    }
  
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/ssl-certificate-checker`,
        { domain: domainToCheck },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
  
      setResult(response.data.result || {});
  
      if (isLoggedIn && user.account_type.toLowerCase() === "free") {
        const creditsResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateCredits`,
          { toolName: "ssl_certificate_checker" }
        );
        setCredits({
          remaining: creditsResponse.data.remaining_credits || 0,
          availed: creditsResponse.data.availed_credits || 0,
          total: creditsResponse.data.total_credits || 0,
          loading: false,
        });
      } else if (isLoggedIn) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/credit/updateUsage`,
          { toolName: "ssl_certificate_checker" }
        );
      }
      setSuccess(response.data.successMessage || "SSL check completed.");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to check SSL certificate. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 2 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            SSL Certificate Checker 🔐
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Enter a domain to fetch SSL certificate details
          </SubtitleText>

          <Box mb={3} sx={{ maxWidth: 400, mx: "auto" }}>
            <TextField
              fullWidth
              label="Enter Domain"
              variant="outlined"
              value={domain}
              onChange={handleDomainChange}
              placeholder="e.g. example.com"
              size="medium"
              id="domain-input"
              name="primary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
              {success}
            </Alert>
          )}

          {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              mt={2}
            >
              <b>⚠️ Free Plan Limitation:</b> 1 credit will be used per check.
              Paid plan users get unlimited checks 🎁
            </Typography>
          ) : (
            isLoggedIn && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={2}
              >
                <b>
                  🎁 SSL checks are free for all Paid users — no credits needed!
                </b>
              </Typography>
            )
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleCheckSSL}
            >
              Check SSL
            </StyledButton>
          </Box>

          {result && (
            <Box mt={4} display="flex" justifyContent="center">
              <Box
                sx={{
                  p: 4,
                  maxWidth: 500,
                  width: "100%",
                  border: "1px solid #ddd",
                  borderRadius: "16px",
                  backgroundColor: "#fafafa",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  textAlign: "center",
                }}
              >
                <Typography variant="h5" fontWeight="bold" mb={3}>
                  📄 SSL Certificate Details
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    justifyItems: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography>
                    <b>Domain</b>
                  </Typography>
                  <Typography>{result.domain || "N/A"}</Typography>

                  <Typography>
                    <b>Issuer</b>
                  </Typography>
                  <Typography>{result.issuer || "N/A"}</Typography>

                  <Typography>
                    <b>Valid From</b>
                  </Typography>
                  <Typography>{result.validFrom || "N/A"}</Typography>

                  <Typography>
                    <b>Valid To</b>
                  </Typography>
                  <Typography>{result.validTo || "N/A"}</Typography>

                  <Typography>
                    <b>Days Remaining</b>
                  </Typography>
                  <Typography>{result.daysRemaining ?? "N/A"}</Typography>

                  <Typography>
                    <b>Status</b>
                  </Typography>
                  <Typography
                    color={result.valid ? "success.main" : "error.main"}
                  >
                    {result.valid ? "✅ Valid" : "❌ Expired or Invalid"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <br />
              Need help?{" "}
              <Link to="/contact" color="primary">
                Contact support
              </Link>
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
                  toolId="ssl-certificate-checker"
                  user={user}
                  login={login}
                  isLoggedIn={isLoggedIn}
                />
        <SSLCertificateCheckerContent />
      </PageContainer>
    </>
  );
}

export default SSLCertificateChecker;
